import { Weight } from '@rematter/frontend-lib-api-client';

import { convertFromPounds, convertToPounds } from './weights';

/**
 * Calculate net weight by subtracting light weight from heavy weight
 * Returns null if either weight is null or invalid
 */
export const calculateNetWeight = (
  heavyWeight: Weight | null,
  lightWeight: Weight | null,
): Weight | null => {
  if (heavyWeight?.value == null || lightWeight?.value == null) {
    return null;
  }

  // Convert both weights to pounds for accurate calculation
  const heavyPounds = convertToPounds(heavyWeight.value, heavyWeight.unit);
  const lightPounds = convertToPounds(lightWeight.value, lightWeight.unit);
  const netPounds = heavyPounds - lightPounds;

  // Convert result back to heavy weight's unit
  const netValue = convertFromPounds(netPounds, heavyWeight.unit);

  return {
    value: Math.max(0, netValue), // Ensure non-negative
    unit: heavyWeight.unit,
    __typename: 'Weight',
  };
};

/**
 * Add two weights together
 * Returns null if either weight is null or invalid
 */
export const addWeights = (
  weight1: Weight | null,
  weight2: Weight | null,
): Weight | null => {
  if (weight1?.value == null || weight2?.value == null) {
    return null;
  }

  // Convert both weights to pounds for accurate calculation
  const weight1Pounds = convertToPounds(weight1.value, weight1.unit);
  const weight2Pounds = convertToPounds(weight2.value, weight2.unit);
  const totalPounds = weight1Pounds + weight2Pounds;

  // Convert result back to first weight's unit
  const totalValue = convertFromPounds(totalPounds, weight1.unit);

  return {
    value: totalValue,
    unit: weight1.unit,
    __typename: 'Weight',
  };
};

/**
 * Subtract second weight from first weight
 * Returns null if either weight is null or invalid
 */
export const subtractWeights = (
  weight1: Weight | null,
  weight2: Weight | null,
): Weight | null => {
  if (weight1?.value == null || weight2?.value == null) {
    return null;
  }

  // Convert both weights to pounds for accurate calculation
  const weight1Pounds = convertToPounds(weight1.value, weight1.unit);
  const weight2Pounds = convertToPounds(weight2.value, weight2.unit);
  const resultPounds = weight1Pounds - weight2Pounds;

  // Convert result back to first weight's unit
  const resultValue = convertFromPounds(resultPounds, weight1.unit);

  return {
    value: Math.max(0, resultValue), // Ensure non-negative
    unit: weight1.unit,
    __typename: 'Weight',
  };
};

/**
 * Get the numeric value from a Weight object, or return a default
 */
export const getWeightValue = (
  weight: Weight | null,
  defaultValue = 0,
): number => weight?.value ?? defaultValue;

/**
 * Compare two weights (returns true if first is greater than second)
 * Returns false if either weight is null
 */
export const isWeightGreaterThan = (
  weight1: Weight | null,
  weight2: Weight | null,
): boolean => {
  if (weight1?.value == null || weight2?.value == null) {
    return false;
  }

  // Convert both weights to pounds for accurate comparison
  const weight1Pounds = convertToPounds(weight1.value, weight1.unit);
  const weight2Pounds = convertToPounds(weight2.value, weight2.unit);

  return weight1Pounds > weight2Pounds;
};

/**
 * Check if a weight is zero or null/undefined
 */
export const isWeightEmpty = (weight: Weight | null): boolean =>
  !weight || !weight.value || weight.value === 0;
